import React, { Fragment, useEffect, useMemo, useState } from "react";
import { cloneDeep, keyBy, round } from "lodash";
import { PartialDeep } from "type-fest";
import { Form, Formik } from "formik";
import { useSearchParams } from "wouter";
import { FileSpreadsheet } from "lucide-react";

import { DateHelper } from "../../../../common/src";
import { numberFormat } from "../../../../common/src/utils/FormatFunctions";

import { Column, Grid } from "../../../../components/src";
import { AttributionPlot } from "../../../../components/src/charts/AttributionPlot";
import { usePrevious } from "../../../../components/src/common/Utils";
import { InstrumentModelTypeEnum, CurrencyEnum, InstrumentPerformance, Party, PartyAccount, PartyAccountType } from "../../types.generated";
import { DateField, MultipleSelectField, SelectField } from "../../../../components/src/form";
import { getTopBottomInstruments } from "./functions";
import { exportToXlsx } from "../../../../components/src/common/exportToXlsx";

interface PerformanceData {
    // First day special case
    totalReturnDay0: number;
    totalReturn: number;
    series: number[];
    plotSeries: number[];
    waterfall: number;
}

export enum AttributionSelectorEnum {
    CreditRatings = "CreditRatings",
    ModelTypes = "ModelTypes",
    Currencies = "Currencies",
    Instruments = "Instruments",
    Accounts = "Accounts"
}
const selectorToInstrumentProperty = (selector: AttributionSelectorEnum) => {
    // Should selector enum return property directly?
    if (selector === AttributionSelectorEnum.ModelTypes) {
        return "modelType";
    } else if (selector === AttributionSelectorEnum.Currencies) {
        return "currency";
    } else if (selector === AttributionSelectorEnum.Instruments) {
        return "name";
    } else if (selector === AttributionSelectorEnum.CreditRatings) {
        return "creditRating";
    }
    return "";
};

export enum PlotTypeEnum {
    Waterfall = "Waterfall",
    Series = "Series"
}
interface FormData {
    startDate?: string;
    endDate?: string;
    selector?: AttributionSelectorEnum;
    currencies?: CurrencyEnum[];
    accounts?: string[];
    modelTypes?: InstrumentModelTypeEnum[];
    instruments?: string[];
    creditRatings?: string[];
    plotType?: PlotTypeEnum.Series;
}

const MS_PER_DAY = 86400000;

export const daysBetween = (fromDate: string, toDate: string): number => {
    return (new Date(toDate).getTime() - new Date(fromDate).getTime()) / MS_PER_DAY;
};

const numberToDecimalString = (num: number, decimals: number) => {
    return round(num, decimals).toFixed(decimals);
};

const dataToTraces = (
    data: any,
    formData: FormData,
    plotTypeChosen: PlotTypeEnum,
    accountsById: Record<string, PartyAccount>,
    sumLevelAttribution: boolean
): any[] => {
    // This will be input from page
    const groupBy = selectorToInstrumentProperty(formData.selector);
    const groupValues = [];
    if (formData.selector === AttributionSelectorEnum.ModelTypes) {
        groupValues.push(...formData.modelTypes);
    } else if (formData.selector === AttributionSelectorEnum.Currencies) {
        groupValues.push(...formData.currencies);
    } else if (formData.selector === AttributionSelectorEnum.Instruments) {
        groupValues.push(...formData.instruments);
    } else if (formData.selector === AttributionSelectorEnum.Accounts) {
        if (sumLevelAttribution) {
            const accountsByDescription = keyBy(Object.values(accountsById), "description");
            const parentAccountDescriptionsById: Record<string, string> = {};
            for (const description of formData.accounts) {
                const parentAccount = accountsById[accountsByDescription[description].parentAccountId];
                parentAccountDescriptionsById[parentAccount._id] = parentAccount.description;
            }
            groupValues.push(...Object.values(parentAccountDescriptionsById));
        } else {
            groupValues.push(...formData.accounts);
        }
    } else if (formData.selector === AttributionSelectorEnum.CreditRatings) {
        groupValues.push(...formData.creditRatings);
    } else {
        // Should not happen but...
        throw new Error("Unknown AttributionSelector");
    }
    const localData = cloneDeep(data);
    const otherGroup = "Other";

    const groupPerformances: Record<string, PerformanceData> = {};
    // Start with other group
    groupPerformances[otherGroup] = {
        totalReturnDay0: 0,
        totalReturn: 0,
        series: [],
        plotSeries: [],
        waterfall: 0
    };

    for (let i = 0; i < groupValues.length; i++) {
        groupPerformances[groupValues[i]] = {
            totalReturnDay0: 0,
            totalReturn: 0,
            series: [],
            plotSeries: [],
            waterfall: 0
        };
    }

    // Get start index - since we know we have all days it can be calculated

    const groupKeys = Object.keys(groupPerformances);

    if (localData && localData.dates && localData.values && localData.instrumentPerformances) {
        // Calculate return series for each group using total as base to get additive return series
        let startDateIndex = 0;
        let endDateIndex = localData.dates.length;
        for (let i = 0; i < endDateIndex; i++) {
            // Since we loop from i=startDateIndex to i<endDateIndex
            if (localData.dates[i] === formData.startDate) startDateIndex = i;
            if (localData.dates[i] === formData.endDate) endDateIndex = i + 1;
        }
        // just in case but underlying issue with startDate #4478 should solve this
        if (startDateIndex < 0) {
            startDateIndex = 0;
        }
        const seriesFactor = 1 / localData.series[startDateIndex];

        const dates: string[] = [];
        const plotSeries: number[] = [];
        // Loop only relevant dates
        for (let i = startDateIndex; i < endDateIndex; i++) {
            dates.push(localData.dates[i]);
            plotSeries.push(100 * (seriesFactor * localData.series[i] - 1));
            for (let j = 0; j < localData.instrumentPerformances.length; j++) {
                const instrumentPerformance: InstrumentPerformance = localData.instrumentPerformances[j];
                // account
                if (formData.selector === AttributionSelectorEnum.Accounts) {
                    const account = accountsById[instrumentPerformance.accountId];
                    if (account) {
                        let description = account.description;
                        if (sumLevelAttribution) {
                            description = accountsById[account.parentAccountId].description;
                        }

                        const group = groupPerformances[description] ? groupPerformances[description] : groupPerformances[otherGroup];
                        if (i === 0) {
                            group.totalReturn += instrumentPerformance.values[i] - instrumentPerformance.cashFlows[i];
                        } else {
                            group.totalReturn +=
                                instrumentPerformance.values[i] - instrumentPerformance.values[i - 1] - instrumentPerformance.cashFlows[i];
                        }
                    }
                }
                // instrument properties
                else {
                    const instrument = instrumentPerformance.instrument;

                    const group = groupPerformances[instrument[groupBy]]
                        ? groupPerformances[instrument[groupBy]]
                        : groupPerformances[otherGroup];
                    if (i === 0) {
                        group.totalReturn += instrumentPerformance.values[i] - instrumentPerformance.cashFlows[i];
                    } else {
                        group.totalReturn +=
                            instrumentPerformance.values[i] - instrumentPerformance.values[i - 1] - instrumentPerformance.cashFlows[i];
                    }
                }
            }
            // Calculate series and reset group returns
            let stackedValue = 0;
            for (let j = 0; j < groupKeys.length; j++) {
                let seriesValue = 0;
                if (i === 0) {
                    groupPerformances[groupKeys[j]].totalReturnDay0 = groupPerformances[groupKeys[j]].totalReturn;
                    seriesValue = 0;
                } else if (i === startDateIndex) {
                    seriesValue = 0;
                } else {
                    let totalReturn = groupPerformances[groupKeys[j]].totalReturn;
                    // If day two - add first day total return since first day forced to start with no return
                    if (i === 1) totalReturn += groupPerformances[groupKeys[j]].totalReturnDay0;

                    // Assert no division by zero?
                    // if (localData.values[i - 1] === 0) throw new Error("Performance value at date " + dates[i] + " zero. Division by zero!");
                    if (localData.values[i - 1] === 0) {
                        seriesValue = groupPerformances[groupKeys[j]].series[i - startDateIndex - 1];
                    } else {
                        // Scale with cumulative return
                        seriesValue =
                            groupPerformances[groupKeys[j]].series[i - startDateIndex - 1] +
                            (seriesFactor * localData.series[i - 1] * totalReturn) / localData.values[i - 1];
                    }
                }
                stackedValue += seriesValue;
                groupPerformances[groupKeys[j]].series.push(seriesValue);
                groupPerformances[groupKeys[j]].plotSeries.push(100 * stackedValue);
                groupPerformances[groupKeys[j]].totalReturn = 0;
                // Update waterfall to last series value in percent for output
                groupPerformances[groupKeys[j]].waterfall = 100 * seriesValue;
            }
        }
        const traces: any[] = [];
        if (plotTypeChosen === PlotTypeEnum.Series) {
            traces.push({ name: otherGroup, x: dates, y: groupPerformances[otherGroup].plotSeries, fill: "tozeroy", type: "scatter" });
            for (let i = 0; i < groupValues.length; i++) {
                traces.push({
                    name: groupValues[i],
                    x: dates,
                    y: groupPerformances[groupValues[i]].plotSeries,
                    fill: "tonexty",
                    type: "scatter"
                });
            }

            traces.push({ name: "Total", x: dates, y: plotSeries, type: "scatter" });
        } else {
            const x = [otherGroup];
            const y = [groupPerformances[otherGroup].waterfall];
            const measure = ["relative"];
            const text = [numberToDecimalString(groupPerformances[otherGroup].waterfall, 1) + " %"];

            for (let i = 0; i < groupValues.length; i++) {
                x.push(groupValues[i]);
                y.push(groupPerformances[groupValues[i]].waterfall);
                measure.push("relative");
                text.push(numberToDecimalString(groupPerformances[groupValues[i]].waterfall, 1) + " %");
            }

            x.push("Total");
            y.push(plotSeries.slice(-1)[0]);
            measure.push("total");
            text.push(numberToDecimalString(plotSeries.slice(-1)[0], 1) + " %");

            traces.push({
                type: "waterfall",
                orientation: "v",
                measure,
                x,
                textposition: "outside",
                text,
                y,
                connector: {
                    visible: false
                },
                decreasing: { marker: { color: "#D98880" } },
                increasing: { marker: { color: "#76D7C4" } },
                totals: { marker: { color: "#85C1E9" } }
            });
        }
        return traces;
    }

    return [];
};

const yesterday = DateHelper.previousWeekday().toISOString().substring(0, 10);

export const initFormData: FormData = {
    startDate: "2000-01-01",
    endDate: yesterday,
    accounts: [],
    currencies: [],
    modelTypes: [],
    instruments: []
};

interface AttributionGraphProps {
    data: PartialDeep<{
        client?: Partial<Party>;
        riskFreeTimeSeries?: Array<{ dates?: string[]; values?: number[]; type?: string }>;
        performance?: Partial<{
            id: string;
            currency: CurrencyEnum;
            dates: string[];
            values: number[];
            cashFlows: number[];
            series: number[];
            instrumentPerformances: Partial<InstrumentPerformance>[];
        }>;
    }> & {
        topBottomInstruments?: string[];
        accountDescriptions?: string[];
        accountsById?: Record<string, PartyAccount>;
    };
    client: Partial<Party>;
    plotType?: PlotTypeEnum;
    selector?: AttributionSelectorEnum;
    style?: { [key: string]: string };
    showForm?: boolean;
    showXlsxButton?: boolean;
    sumLevelAttribution?: boolean;
}

export const AttributionGraph = ({
    data,
    client,
    plotType,
    selector,
    style,
    showForm = true,
    showXlsxButton = true,
    sumLevelAttribution = false
}: AttributionGraphProps): React.ReactElement => {
    const [queryArgs, setQueryArgs] = useSearchParams();

    // Default to the Series (line/area) plot when no explicit plotType prop is given so the first render
    // doesn't briefly fall into the waterfall/bar branch before the init effect sets plotType=Series.
    const [plotTypeChosen, setPlotTypeChosen] = useState(plotType ?? (showForm ? PlotTypeEnum.Series : null));
    const previousClientId: string = usePrevious(client._id);

    useEffect(() => {
        if (showForm && data) {
            if (client._id && previousClientId && previousClientId !== client._id) {
                setQueryArgs(null);
            } else {
                if (queryArgs.get("plotType") === null) {
                    const init = cloneDeep(initFormData);
                    init.startDate = data.performance.dates.length > 0 ? data.performance.dates[0] : "2000-01-01";
                    init.plotType = PlotTypeEnum.Series;
                    init.selector = AttributionSelectorEnum.ModelTypes;
                    // `setQueryArgs` replaces the whole query string, so a period already present in the URL
                    // (carried over from another performance tab, or from a shared link) has to be kept here
                    // instead of being reset to the full available history.
                    const urlStartDate = queryArgs.get("startDate");
                    const urlEndDate = queryArgs.get("endDate");
                    if (urlStartDate) init.startDate = urlStartDate;
                    if (urlEndDate) init.endDate = urlEndDate;
                    setQueryArgs(init as unknown as Record<string, any>);
                }
            }
        }
        if (queryArgs.get("plotType") && queryArgs.get("plotType") !== plotTypeChosen) {
            setPlotTypeChosen(queryArgs.get("plotType") as PlotTypeEnum);
        }
    }, [client._id, data, plotTypeChosen, previousClientId, queryArgs, setQueryArgs, showForm]);

    const { formData, usedCurrencies, usedModelTypes, usedCreditRatings, usedInstruments, usedAccounts, accountsById, performanceData } =
        useMemo(() => {
            const usedCurrencies: Record<string, boolean> = {};
            const usedModelTypes: Record<string, boolean> = {};
            const usedCreditRatings: Record<string, boolean> = {};
            const usedInstruments: Record<string, boolean> = {};
            const lastInstrumentValueByName: Record<string, number> = {};
            const instrumentPerformances = data ? data.performance.instrumentPerformances : ([] as InstrumentPerformance[]);

            for (const instrumentPerformance of instrumentPerformances) {
                if (instrumentPerformance && instrumentPerformance.instrument && instrumentPerformance.instrument.currency) {
                    usedCurrencies[instrumentPerformance.instrument.currency] = true;
                }
                if (instrumentPerformance && instrumentPerformance.instrument && instrumentPerformance.instrument.modelType) {
                    usedModelTypes[instrumentPerformance.instrument.modelType] = true;
                }
                if (instrumentPerformance && instrumentPerformance.instrument) {
                    usedInstruments[instrumentPerformance.instrument.name] = true;
                    lastInstrumentValueByName[instrumentPerformance.instrument.name] =
                        instrumentPerformance.series[instrumentPerformance.series.length - 1];
                }
                if (
                    instrumentPerformance &&
                    instrumentPerformance.instrument &&
                    instrumentPerformance.instrument.modelType &&
                    instrumentPerformance.instrument.modelType === InstrumentModelTypeEnum.Bond &&
                    instrumentPerformance.instrument.creditRating
                ) {
                    usedCreditRatings[instrumentPerformance.instrument.creditRating] = true;
                }
            }

            const accounts = data && data.client ? data.client.accounts : ([] as PartyAccount[]);
            const usedAccounts: Record<string, boolean> = {};
            const accountsById: Record<string, PartyAccount> = {};

            if (data && data.accountsById) {
                for (const [accountId, account] of Object.entries(data.accountsById)) {
                    if (account.description) accountsById[accountId] = account;
                }
            }

            for (const account of accounts) {
                if (account && account.type === PartyAccountType.Physical && account.description) {
                    usedAccounts[account.description] = true;
                }
                if (account && account.description) accountsById[account._id] = account as any;
            }

            let usedSelector = AttributionSelectorEnum.ModelTypes;
            // if more than 3 physical accounts use accounts
            if (Object.values(usedAccounts).length > 3) usedSelector = AttributionSelectorEnum.Accounts;
            // selector specified in args
            if (queryArgs.get("selector")) usedSelector = queryArgs.get("selector") as AttributionSelectorEnum;
            // selector specified in params
            if (selector) usedSelector = selector;

            const formData: FormData = {
                startDate: queryArgs.get("startDate") ? (queryArgs.get("startDate") as string) : yesterday,
                endDate: queryArgs.get("endDate") ? (queryArgs.get("endDate") as string) : yesterday,
                selector: usedSelector,
                accounts: [],
                currencies: [],
                modelTypes: [],
                instruments: [],
                creditRatings: [],
                plotType: PlotTypeEnum.Series
            };

            if (queryArgs.get("currencies")) {
                if (queryArgs.get("currencies").includes(",")) {
                    formData.currencies = queryArgs.get("currencies").split(",") as unknown as CurrencyEnum[];
                } else {
                    formData.currencies = [queryArgs.get("currencies") as CurrencyEnum];
                }
            }

            if (queryArgs.get("modelTypes")) {
                if (queryArgs.get("modelTypes").includes(",")) {
                    formData.modelTypes = queryArgs.get("modelTypes").split(",") as unknown as InstrumentModelTypeEnum[];
                } else {
                    formData.modelTypes = [queryArgs.get("modelTypes") as InstrumentModelTypeEnum];
                }
            }

            if (queryArgs.get("creditRatings")) {
                if (queryArgs.get("creditRatings").includes(",")) {
                    formData.creditRatings = queryArgs.get("creditRatings").split(",") as unknown as string[];
                } else {
                    formData.creditRatings = [queryArgs.get("creditRatings")];
                }
            }

            if (queryArgs.get("instruments")) {
                if (!queryArgs.get("instruments").includes(",")) {
                    formData.instruments = [queryArgs.get("instruments")];
                } else {
                    const instruments = queryArgs.get("instruments").split(",");
                    if (instruments.length > 10 && showForm) {
                        const usedInstrumentsValues: { [key: string]: number } = {};
                        for (const instrumentName of instruments) {
                            if (lastInstrumentValueByName[instrumentName]) {
                                usedInstrumentsValues[instrumentName] = lastInstrumentValueByName[instrumentName];
                            }
                        }

                        const topBottomInstruments = getTopBottomInstruments(usedInstrumentsValues);
                        formData.instruments = topBottomInstruments;
                    } else {
                        formData.instruments = instruments;
                    }
                }
            }

            if (queryArgs.get("accounts")) {
                if (!queryArgs.get("accounts").includes(",")) {
                    const accounts = queryArgs.get("accounts").split(",");
                    formData.accounts = accounts;
                } else {
                    formData.accounts = [queryArgs.get("accounts")];
                }
            }

            if (data && data.topBottomInstruments) {
                formData.instruments = data.topBottomInstruments;
            }
            if (data && data.accountDescriptions) {
                formData.accounts = data.accountDescriptions;
            }
            const performanceData = data ? data.performance : {};
            return {
                formData,
                usedCurrencies,
                usedModelTypes,
                usedCreditRatings,
                usedInstruments,
                usedAccounts,
                accountsById,
                performanceData
            };
        }, [data, queryArgs, selector, showForm]);

    const attributionYLabel = "Cumulative return [%]";

    let traces = [];

    if (Object.keys(performanceData) && Object.keys(performanceData).length) {
        traces = dataToTraces(performanceData, formData as unknown as FormData, plotTypeChosen, accountsById, sumLevelAttribution);
    }

    if (!queryArgs.toString()) {
        //console.error("queryArgs", queryArgs);
        return <div></div>;
    }

    const nonStackedTraces: { name: string; value: number }[] = [];
    let previousValue = 0;
    if (traces.length) {
        if (traces[0].type === "scatter") {
            for (let i = 0; i < traces.length; i++) {
                let value: number = traces[i] && traces[i].y && traces[i].y.length ? Number(traces[i].y[traces[i].y.length - 1]) : null;
                if (value || value === 0) {
                    const stackedValue = value;
                    value = i === traces.length - 1 ? stackedValue : stackedValue - previousValue;
                    previousValue = stackedValue;
                }
                const name: string = traces[i].name;
                nonStackedTraces.push({ name, value: value || value === 0 ? value : null });
            }
        } else if (traces[0].type === "waterfall") {
            for (let i = 0; i < traces[0].x.length; i++) {
                const value: number = traces[0] && traces[0].y ? Number(traces[0].y[i]) : null;
                const name: string = traces[0].x[i];
                nonStackedTraces.push({ name, value: value || value === 0 ? value : null });
            }
        }
    }

    const currencyOptions: {
        key: string;
        value: string;
    }[] = Object.keys(usedCurrencies)
        .sort()
        .map((v) => {
            return { key: v, value: v };
        });

    const modelTypesOptions: {
        key: string;
        value: string;
    }[] = Object.keys(usedModelTypes)
        .sort()
        .map((v) => {
            return { key: v, value: v };
        });

    const creditRatingOptions: {
        key: string;
        value: string;
    }[] = Object.keys(usedCreditRatings)
        .sort()
        .map((v) => {
            return { key: v, value: v };
        });

    const instrumentsOptions: {
        key: string;
        value: string;
    }[] = Object.keys(usedInstruments)
        .sort()
        .map((v) => {
            return { key: v, value: v };
        });

    const accountOptions: {
        key: string;
        value: string;
    }[] = Object.keys(usedAccounts)
        .sort()
        .map((v) => {
            return { key: v, value: v };
        });

    return (
        <div className="w-full">
            <div className={!showForm ? "row-fluid" : "flex flex-wrap -mx-2"}>
                {showForm ? (
                    <div className="w-full sm:w-1/3 md:w-1/4 px-2 mb-4">
                        <Formik
                            enableReinitialize={true}
                            initialValues={formData}
                            validate={(validateFormData) => {
                                const errors: any = {};

                                if (validateFormData.selector === AttributionSelectorEnum.Currencies) {
                                    delete validateFormData.instruments;
                                    delete validateFormData.modelTypes;
                                    delete validateFormData.accounts;
                                    delete validateFormData.creditRatings;
                                } else if (validateFormData.selector === AttributionSelectorEnum.ModelTypes) {
                                    delete validateFormData.instruments;
                                    delete validateFormData.currencies;
                                    delete validateFormData.accounts;
                                    delete validateFormData.creditRatings;
                                } else if (validateFormData.selector === AttributionSelectorEnum.Instruments) {
                                    delete validateFormData.modelTypes;
                                    delete validateFormData.currencies;
                                    delete validateFormData.accounts;
                                    delete validateFormData.creditRatings;
                                } else if (validateFormData.selector === AttributionSelectorEnum.CreditRatings) {
                                    delete validateFormData.instruments;
                                    delete validateFormData.modelTypes;
                                    delete validateFormData.currencies;
                                    delete validateFormData.accounts;
                                } else if (validateFormData.selector === AttributionSelectorEnum.Accounts) {
                                    delete validateFormData.modelTypes;
                                    delete validateFormData.currencies;
                                    delete validateFormData.instruments;
                                    delete validateFormData.creditRatings;
                                } else {
                                    validateFormData.selector = AttributionSelectorEnum.Currencies;
                                    validateFormData.currencies = [];
                                    delete validateFormData.instruments;
                                    delete validateFormData.modelTypes;
                                    delete validateFormData.creditRatings;
                                }

                                setQueryArgs(validateFormData as unknown as Record<string, any>);
                                return Object.keys(errors).length > 0 ? errors : {};
                            }}
                            onSubmit={null}
                        >
                            {({ isSubmitting }) => (
                                <Fragment>
                                    <Form autoComplete="off">
                                        <div className="w-full form print-none">
                                            <div className="flex flex-wrap -mx-2">
                                                <div className="w-full px-2 mb-4">
                                                    <DateField
                                                        className=""
                                                        name="startDate"
                                                        label="Start date"
                                                        minDate={client.firstTradeDate}
                                                        disabled={isSubmitting}
                                                    />
                                                    <DateField
                                                        className=""
                                                        name="endDate"
                                                        label="End date"
                                                        minDate={client.firstTradeDate}
                                                        disabled={isSubmitting}
                                                    />
                                                    <SelectField
                                                        name="plotType"
                                                        label="Plot type"
                                                        options={Object.keys(PlotTypeEnum)
                                                            .sort()
                                                            .map((option) => ({ key: option, value: option }))}
                                                        className=""
                                                        disabled={isSubmitting}
                                                        size={Object.keys(PlotTypeEnum).length}
                                                    />
                                                    <SelectField
                                                        name="selector"
                                                        label="Selector"
                                                        options={Object.keys(AttributionSelectorEnum)
                                                            .sort()
                                                            .map((option) => ({ key: option, value: option }))}
                                                        className=""
                                                        disabled={isSubmitting}
                                                        size={Object.keys(AttributionSelectorEnum).length}
                                                    />
                                                    {formData.selector.toString() === AttributionSelectorEnum.Currencies.toString() ? (
                                                        <MultipleSelectField
                                                            name="currencies"
                                                            label="Currencies"
                                                            options={currencyOptions}
                                                            className=""
                                                            disabled={isSubmitting}
                                                            size={Object.values(currencyOptions).length}
                                                        />
                                                    ) : null}
                                                    {formData.selector.toString() === AttributionSelectorEnum.ModelTypes.toString() ? (
                                                        <MultipleSelectField
                                                            name="modelTypes"
                                                            label="Model types"
                                                            options={modelTypesOptions}
                                                            className=""
                                                            disabled={isSubmitting}
                                                            size={Object.values(modelTypesOptions).length}
                                                        />
                                                    ) : null}
                                                    {formData.selector.toString() === AttributionSelectorEnum.Instruments.toString() ? (
                                                        <MultipleSelectField
                                                            name="instruments"
                                                            label="Instruments"
                                                            options={instrumentsOptions}
                                                            className=""
                                                            disabled={isSubmitting}
                                                            size={Object.values(instrumentsOptions).length}
                                                        />
                                                    ) : null}
                                                    {formData.selector.toString() === AttributionSelectorEnum.CreditRatings.toString() ? (
                                                        <MultipleSelectField
                                                            name="creditRatings"
                                                            label="Credit Ratings"
                                                            options={creditRatingOptions}
                                                            className=""
                                                            disabled={isSubmitting}
                                                            size={Object.values(creditRatingOptions).length}
                                                        />
                                                    ) : null}
                                                    {formData.selector.toString() === AttributionSelectorEnum.Accounts.toString() ? (
                                                        <MultipleSelectField
                                                            name="accounts"
                                                            label="Accounts"
                                                            options={accountOptions}
                                                            className=""
                                                            disabled={isSubmitting}
                                                            size={Object.values(accountOptions).length}
                                                        />
                                                    ) : null}
                                                    <Grid data={nonStackedTraces} className="table-timeseries" tableClassName="table-xs">
                                                        <Column field="name" title="Group" className="nowrap left" />
                                                        <Column
                                                            field="value"
                                                            title="Cum return"
                                                            className="text-right"
                                                            format={(value) => (
                                                                <div className="text-right">{numberFormat(Number(value), "# ##0.00")}%</div>
                                                            )}
                                                        />
                                                    </Grid>
                                                </div>
                                            </div>
                                        </div>
                                    </Form>
                                </Fragment>
                            )}
                        </Formik>
                    </div>
                ) : null}

                {Object.keys(performanceData) && Object.keys(performanceData).length ? (
                    <div className={!showForm ? "fluid-col-sm-8 fluid-col-md-9" : "w-full sm:w-2/3 md:w-3/4 px-2 mb-4"}>
                        <h4 className="text-center">Performance attribution</h4>
                        <div className="mb-3" style={{ width: "20px", height: "20px" }}>
                            {showXlsxButton ? (
                                <div
                                    className="print-none"
                                    onClick={() => {
                                        const data: Record<string, Record<string, string | number>> = {};
                                        traces.forEach((trace) => {
                                            for (const index in trace.x) {
                                                const date = trace.x[index];
                                                const value = trace.y[index];
                                                if (data[date]) {
                                                    data[date][trace.name] = value;
                                                } else {
                                                    const timeSeriesItem: Record<string, string | number> = {};
                                                    timeSeriesItem["Date"] = date;
                                                    timeSeriesItem[trace.name] = value;
                                                    data[date] = timeSeriesItem;
                                                }
                                            }
                                        });
                                        exportToXlsx([Object.values(data)], "attribution.xlsx", ["attribution"]);
                                    }}
                                >
                                    <FileSpreadsheet className="Excel" />
                                </div>
                            ) : null}
                        </div>
                        <AttributionPlot
                            traces={traces}
                            yLabel={attributionYLabel}
                            height={480}
                            style={{
                                width: "100%",
                                height: style && style.height ? style.height : "calc(100vh - 100px)",
                                position: "sticky",
                                top: "0"
                            }}
                        />
                    </div>
                ) : null}
            </div>
        </div>
    );
};
